import {
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { SiteModel } from 'src/app/models/site.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SitesService } from 'src/app/shared/sites.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { faHardHat, faFirstAid } from '@fortawesome/free-solid-svg-icons';
import { ApiRequestService } from '../../../../shared/api-request.service';
import { RBACService } from '../../../../shared/rbac.service';

@Component({
  selector: 'app-sites-eep-edit',
  templateUrl: './sites-eep-edit.component.html',
  styleUrls: ['./sites-eep-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesEepEditComponent implements OnInit {
  site: SiteModel = new SiteModel();

  faHardHat = faHardHat;
  faFirstAid = faFirstAid;

  inProgress: boolean = false;

  @ViewChild('emergencyEvacuationPlanMapFileSelector')
  emergencyEvacuationPlanMapFileSelector: ElementRef;

  parent_id: number;
  child_id: number;
  site_id: number;

  // Mobile field Country Code selector values
  phone_types: string[] = [
    'emergency',
    'hospital',
    'medical_centre',
    'worksafe',
    'civil_defence',
    'poison_centre',
    'power',
    'faults'
  ];
  phone_input_id = {};
  phone_number = {};
  phone_country_code = {};
  phone_dial_code = {};
  phone_e164 = {};
  phone_error_state = {};

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDeleteEep: boolean = false;
  canEditEep: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private siteService: SitesService,
    public utils: UtilsService,
    private router: Router,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    // Resolve RBAC permission flags before any early returns so they are always set.
    this.canDeleteEep = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_EEP,
        RBACService.RBAC_A.A_DELETE
      )
    ]);

    // Edit mode needs Sites Edit as well as Sites: EEP Edit, because the EEP text fields are saved
    // through the site update endpoint (SitesController::update), which requires sites:edit.
    this.canEditEep = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_SITES, RBACService.RBAC_A.A_EDIT),
      RBACService.getPermission(RBACService.RBAC_F.F_SITES_EEP, RBACService.RBAC_A.A_EDIT)
    ], true);

    // Resolve the route ids before any redirect, because the view link is built from them.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);
    const is_existing_site: boolean = !!(
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    );
    if (is_existing_site) {
      this.site_id = this.child_id ? this.child_id : this.parent_id;
    }

    if (!this.canEditEep) {
      // Users who can see the plan but not save it are sent to view mode. sites:view is implied
      // by the site context (the parent layout enforces it).
      const canViewEep: boolean = this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_SITES_EEP, RBACService.RBAC_A.A_VIEW),
        RBACService.getPermission(RBACService.RBAC_F.F_SITES_EEP, RBACService.RBAC_A.A_EDIT)
      ]);

      if (!canViewEep || !this.site_id) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to view the emergency evacuation plan.');
        this.router.navigate(['/sites']);
        return;
      }

      this.router.navigate([
        this.utils.prepareSiteRelatedLink(
          this.site_id,
          this.child_id ? this.parent_id : null,
          'view/emergency-evacuation-plans'
        )
      ]);

      return;
    }

    // Generate phone input ids
    for (let i: number = 0; i < this.phone_types.length; i++) {
      this.phone_input_id[this.phone_types[i]] = `phone-${i + 1}`;
    }

    // Load the site when editing an existing one (site_id was resolved above).
    if (is_existing_site) {
      this.site.id = this.site_id;

      this.siteService.find(this.site.id).subscribe(
        (response): void => {
          this.site = response.data;
        }
      );
    }
  }

  onSubmit(): void {
    if (!this.canEditEep) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit the emergency evacuation plan.');
      return;
    }

    // Check Error states of phone inputs, then continue submission
    if (this.getPhoneDetailsErrorState()) {
      this.utils.showModal(
        'Form Validation Failed',
        'A Phone number must be at least 3 characters long.'
      );
      return;
    }

    // Terminate early if another process is in progress.
    if (this.inProgress) {
      return;
    }

    // Update Site details with Mobile field Country Code selector values
    for (let i = 0; i < this.phone_types.length; i++) {
      this.site[this.phone_types[i] + '_phone'] = this.phone_number[
        this.phone_types[i]
      ];
      this.site[
        this.phone_types[i] + '_phone_country_code'
      ] = this.phone_country_code[this.phone_types[i]];
      this.site[
        this.phone_types[i] + '_phone_dial_code'
      ] = this.phone_dial_code[this.phone_types[i]];
      this.site[this.phone_types[i] + '_phone_e164'] = this.phone_e164[
        this.phone_types[i]
      ];
    }

    this.inProgress = true;

    this.siteService.update(this.site).subscribe(
      (): void => {
        this.utils.showToast('Updated Emergency Evacuation Plan');
        this.inProgress = false;
      },
      () => {
        this.inProgress = false;
      }
    );
  }

  /**
   * Get Output values from phone input component.
   */
  onGetOutputPhoneDetails($event, $type): void {
    if ($event) {
      let phone = $event;
      this.phone_number[$type] =
        phone.hasOwnProperty('number') && phone.number !== null
          ? phone.number
          : '';
      this.phone_country_code[$type] =
        phone.hasOwnProperty('countryCode') && phone.countryCode !== null
          ? phone.countryCode
          : '';
      this.phone_dial_code[$type] =
        phone.hasOwnProperty('dialCode') && phone.dialCode !== null
          ? phone.dialCode
          : '';
      this.phone_e164[$type] =
        phone.hasOwnProperty('e164Number') && phone.e164Number !== null
          ? phone.e164Number
          : '';
      this.phone_error_state[$type] =
        phone.hasOwnProperty('errorState') && phone.errorState !== null
          ? phone.errorState
          : true;
    }
  }

  /**
   * Return Error state from all phone inputs.
   */
  getPhoneDetailsErrorState(): boolean {
    let errorState: boolean = false;
    for (let i: number = 0; i < this.phone_types.length; i++) {
      if (this.phone_error_state[this.phone_types[i]]) {
        errorState = true;
        break;
      }
    }
    return errorState;
  }

  /**
   * Dispatch a click event for the user to select an image file from their device.
   */
  onSelectEmergencyEvacuationPlanMap(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (this.emergencyEvacuationPlanMapFileSelector) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.emergencyEvacuationPlanMapFileSelector.nativeElement.dispatchEvent(
        clickEvt
      );
    }
  }

  /**
   * Make an API request to upload a new image file to be stored as an emergency evacuation plan map.
   * @param evt
   */
  onUploadEmergencyEvacuationPlanMap(evt: any): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (evt.target.files.length == 0) {
      // The user likely canceled the action.
      return;
    }
    // Make the file upload request.
    this.api
      .makeUploadRequest(
        `v1/sites/${this.site.id}/emergency-evacuation-plan-map`,
        evt.target.files[0]
      )
      .then(
        (response: SiteModel): void => {
          // Replace teh site object.
          this.site = response as SiteModel;
          // Show a success message.
          this.utils.showToast(
            'Your emergency evacuation plan map was uploaded.'
          );
        }
      )
      .catch(
        (errorResponse: any): void => {
          this.utils.handleAPIErrors(errorResponse);
        }
      );
  }

  /**
   * Initiates the download of the emergency evacuation plan map for the specified site.
   *
   * @param {SiteModel} site - The site model object containing details of the site, including the evacuation map URL.
   * @return {void} This method does not return a value.
   */
  onDownloadEmergencyEvacuationPlanMap(site: SiteModel): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.onDownloadFromTemporaryURL(
      site.evacuation_map_url,
      'Emergency Evacuation Plan Map.' +
        site.evacuation_map_url.split('.').pop()
    );
  }

  /**
   * Make an API request to remove the emergency evacuation plan map from the site.
   * @param evt
   */
  onRemoveEmergencyEvacuationPlanMap(evt: any): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    // Check if the delete button was pressed intentionally.
    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to remove the emergency evacuation plan map?',
      [
        {
          text: 'Cancel',
          handler: (): void => {}
        },
        {
          text: 'Yes',
          handler: (): void => {
            // Make an API request to remove the emergency evacuation plan map.
            this.api
              .makeRequest(
                'delete',
                `v1/sites/${this.site.id}/emergency-evacuation-plan-map`
              )
              .then(
                (response: SiteModel): void => {
                  // Replace the site object with the response.
                  this.site = response as SiteModel;
                  // Show a success message.
                  this.utils.showToast(
                    'Your emergency evacuation plan map was removed.'
                  );
                }
              )
              .catch(
                (errorResponse: any): void => {
                  this.utils.handleAPIErrors(errorResponse);
                }
              );
          }
        }
      ]
    );
  }

  /**
   * Export the emergency evacuation plan PDF document.
   */
  onExportEmergencyEvacuationPlan(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.api
      .makeRequest('get', `v1/sites/${this.site.id}/emergency-evacuation-plan`)
      .then(
        (response: any): void => {
          this.utils.openUrl(response.data.url);
        }
      );
  }

  /**
   * @deprecated
   * This method is kept for backward compatability and should be removed in future versions of the web portal.
   * Get and store a single site safety manager.
   */
  onSelectSafetyManager(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_NETWORKED_USERS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: [this.site.safety_manager_id],
        visitor_site_id: this.site.id
      },
      {},
      (result: number): void => {
        if (!result) {
          return;
        }
        this.api.makeRequest('get', `v1/user/${result}`).then(
          (response: any): void => {
            this.site.safety_manager = response.data;
            this.site.safety_manager_id = result;
          }
        );
      }
    );
  }

  /**
   * @deprecated
   * This method is kept for backward compatability and should be removed in future versions of the web portal.
   * Get and store a single trained first-aider.
   */
  onSelectTrainedFirstAider(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_NETWORKED_USERS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        multiple: false,
        selected: [this.site.trained_first_aider_id],
        visitor_site_id: this.site.id
      },
      {},
      (result: number): void => {
        if (!result) {
          return;
        }
        this.api.makeRequest('get', `v1/user/${result}`).then(
          (response: any): void => {
            this.site.trained_first_aider = response.data;
            this.site.trained_first_aider_id = result;
          }
        );
      }
    );
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void} This method does not return a value.
   */
  onOpenUrl(url: string): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.openUrl(url);
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }

  protected readonly RBACService = RBACService;
}
